import { useQueryClient } from '@tanstack/react-query';
import type { AiOperationDto } from '@scoutaround/script-breakdown-shared';

import { AiCostApproval } from './AiCostApproval.js';
import { CreditBalanceChip } from './CreditBalanceChip.js';
import { pagesLabel } from './format.js';

import '../../styles/credits.css';

/**
 * "NEEDS READING — THIS PDF IS A SCAN" (P6, S6).
 *
 * A PDF with no text layer is stored and parsed for free like any other, and
 * the parse stops here: its scenes can only be found by an AI reading of every
 * page, which is paid from the production's credits and runs only once
 * somebody approves its estimate (`SCRIPT_OCR`). Nothing reads it before then.
 *
 * Shown wherever the screenplay's state is shown — step 1 and the Script tab —
 * from the one field the current script carries for it (`paidReading`).
 */
export function NeedsReadingPanel({
  projectId,
  scriptVersionId,
  pageCount,
  onStarted,
}: {
  projectId: string;
  scriptVersionId: string;
  pageCount: number;
  onStarted?: (operation: AiOperationDto) => void;
}) {
  const queryClient = useQueryClient();

  return (
    <div className="credits-needs-reading" data-testid="needs-reading">
      <div className="credits-panel-head">
        <p className="colh" style={{ margin: 0 }} data-testid="needs-reading-title">
          Needs reading — this PDF is a scan
        </p>
        <CreditBalanceChip projectId={projectId} />
      </div>
      <p className="mm credits-note" data-testid="needs-reading-text">
        {`It has no text layer, so its ${pagesLabel(pageCount)} ${pageCount === 1 ? 'has' : 'have'} to be read by AI before its scenes can be found. The reading is paid from the production’s credits and starts only when you approve it.`}
      </p>

      <AiCostApproval
        projectId={projectId}
        operationType="SCRIPT_OCR"
        scriptVersionId={scriptVersionId}
        testId="needs-reading-approval"
        onStarted={(operation) => {
          // The reading's progress and the scenes it finds arrive on the
          // screenplay's own answers; both are stale the moment it is approved.
          void Promise.all([
            queryClient.invalidateQueries({ queryKey: ['current-script', projectId] }),
            queryClient.invalidateQueries({ queryKey: ['script-progress', scriptVersionId] }),
          ]);
          onStarted?.(operation);
        }}
      />
    </div>
  );
}
